import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  onFetchCart,
  onFetchCartSuccess,
  onCartProductDetailsSuccess,
  onFetchCartCount,
  onFetchCartCountSuccess,
  onFetchCartComplete,
  onFetchCartError,
  onAddToCart,
  onRemoveCartLines,
  onAddToCartSuccess,
  onAddToCartError,
} from "../Store/Slices/cartSlice";
import { callFetchCartApi, callCartProductDetailsApi } from "../Context/cartApi";
import { callAddToCartPostApi } from "../Context/addToCartApi";
import { decryptAES } from "../Common/Axios/encrypt";
import { toAddToCartPayload, toCartLines, toProductDetails, toSetLinesPayload } from "../Context/cartMapper";

const DEFAULT_CART_DETAILS_CACHE_SECONDS = 30;

/* How long fetched product details stay fresh, in milliseconds.
   Set REACT_APP_CART_DETAILS_CACHE_SECONDS in the env file to change it. */
const getCacheWindowMs = () => {
  const seconds = Number(process.env.REACT_APP_CART_DETAILS_CACHE_SECONDS);
  return (Number.isFinite(seconds) && seconds >= 0
    ? seconds
    : DEFAULT_CART_DETAILS_CACHE_SECONDS) * 1000;
};

/* Cached details can be reused when they are still inside the cache window
   and every SKU now in the cart was part of that fetch. SKUs the API did not
   return (e.g. disabled products) count as fetched, so they do not force a
   new call on every open. */
const canReuseCachedDetails = (cartState, skuIds) => {
  const { productDetailsFetchedAt, productDetailsSkus = [] } = cartState;
  if (!productDetailsFetchedAt) return false;
  const isFresh = Date.now() - productDetailsFetchedAt < getCacheWindowMs();
  return isFresh && skuIds.every((sku) => productDetailsSkus.includes(sku));
};

/* API error text when present; the page shows a translated fallback otherwise.
   Error bodies come back encrypted, as addToCartSaga handles them. */
const errorMessageFrom = (error) => {
  const body = error?.response?.data;
  try {
    const parsed = typeof body === "string" ? decryptAES(body) : body;
    return parsed?.errorMessage || parsed?.ErrorMessage || "";
  } catch {
    return "";
  }
};

function* fetchCart({ payload }) {
  try {
    const cartResponse = yield call(callFetchCartApi, payload?.memberId);
    if (cartResponse?.httpStatusCode !== "200") {
      yield put(onFetchCartError({ message: cartResponse?.errorMessage }));
      return;
    }

    const items = toCartLines(cartResponse?.response);
    yield put(onFetchCartSuccess({ items }));

    // Only lines with a SKU can be looked up in the product list.
    const skuIds = [...new Set(items.map((item) => item.sku).filter(Boolean))];

    // Empty cart (or nothing to look up): product details are not needed.
    if (skuIds.length === 0) {
      yield put(onFetchCartComplete());
      return;
    }

    const cartState = yield select((state) => state.cartReducer);
    if (canReuseCachedDetails(cartState, skuIds)) {
      yield put(onFetchCartComplete());
      return;
    }

    const detailsResponse = yield call(callCartProductDetailsApi, skuIds);
    if (detailsResponse?.httpStatusCode === "200") {
      yield put(
        onCartProductDetailsSuccess({
          data: (detailsResponse?.response || []).map(toProductDetails),
          skuIds,
          fetchedAt: Date.now(),
        })
      );
    } else {
      yield put(onFetchCartError({ message: detailsResponse?.errorMessage }));
    }
  } catch (error) {
    yield put(onFetchCartError({ message: errorMessageFrom(error) }));
  }
}

/* Header count: the cart lines only. Errors are ignored so the header keeps
   the last count instead of showing an error. */
function* fetchCartCount({ payload }) {
  try {
    const cartResponse = yield call(callFetchCartApi, payload?.memberId);
    if (cartResponse?.httpStatusCode === "200") {
      yield put(onFetchCartCountSuccess({ items: toCartLines(cartResponse?.response) }));
    }
  } catch {
    // Keep the last known count.
  }
}

/* Sets one line to qty. The API returns the whole cart, which replaces
   the lines; the SKU set does not change, so cached details stay valid. */
function* addToCart({ payload }) {
  try {
    const { memberId, line, qty } = payload;
    // POST /user-Cart returns the updated CartDto with the line set to qty.
    const response = yield call(callAddToCartPostApi, toAddToCartPayload(memberId, line, qty));
    if (response?.httpStatusCode === "200") {
      yield put(onAddToCartSuccess({ items: toCartLines(response?.response) }));
    } else {
      yield put(onAddToCartError({ message: response?.errorMessage }));
    }
  } catch (error) {
    yield put(onAddToCartError({ message: errorMessageFrom(error) }));
  }
}

/* Removes several lines in one call (quantity 0); same result handling as a single line. */
function* removeCartLines({ payload }) {
  try {
    const { memberId, lines } = payload;
    const response = yield call(callAddToCartPostApi, toSetLinesPayload(memberId, lines, 0));
    if (response?.httpStatusCode === "200") {
      yield put(onAddToCartSuccess({ items: toCartLines(response?.response) }));
    } else {
      yield put(onAddToCartError({ message: response?.errorMessage }));
    }
  } catch (error) {
    yield put(onAddToCartError({ message: errorMessageFrom(error) }));
  }
}

export default function* cartSaga() {
  yield takeLatest(onFetchCart.type, fetchCart);
  yield takeLatest(onAddToCart.type, addToCart);
  yield takeLatest(onRemoveCartLines.type, removeCartLines);
  yield takeLatest(onFetchCartCount.type, fetchCartCount);
}
